import { APP_CHAIN } from '../confs/app';
import { publicClient } from '../utils/chain-client';
import { useSignedUserInfo } from './use-signed-user-info';
import { useBaseTokens } from '@/libs/react-sdk/api/use-base-tokens';
import { ApiInstance } from '@/libs/sdk/api';
import { useQuery } from '@tanstack/react-query';
import { Address, Chain, erc20Abi } from 'viem';
import { multicall } from 'viem/actions';

type TokenBalanceParams = {
  chain?: Chain;
};

// The balance changes only on the signer's own trades and transfers. A trade
// refreshes it at once (trade-success-queries.ts); after a transfer it can be
// up to 60 s old (the poll, or a window focus once the data is stale).
const BALANCE_REFETCH_INTERVAL = 60_000;

export const useSingerTokenBalance = (api: ApiInstance, { chain = APP_CHAIN }: TokenBalanceParams = {}) => {
  const { data: userInfo } = useSignedUserInfo(api);
  const address = userInfo?.eoaAddress;
  const { data: tokens } = useBaseTokens(api);
  const query = useQuery({
    enabled: !!address && !!tokens,
    refetchInterval: BALANCE_REFETCH_INTERVAL,
    staleTime: BALANCE_REFETCH_INTERVAL,
    queryKey: ['SingerTokenBalance', address, tokens, chain?.id],
    queryFn: async () => {
      if (!tokens?.baseTokens) return [];
      // Filter out tokens without valid addresses (e.g. Points token)
      const validTokens = tokens.baseTokens.filter(
        token => token.baseTokenAddress && token.baseTokenAddress.startsWith('0x')
      );
      if (validTokens.length === 0) return tokens.baseTokens.map(token => ({ ...token, balance: 0n }));
      const result = await multicall(publicClient, {
        allowFailure: true,
        contracts: validTokens.map(token => ({
          address: token.baseTokenAddress as Address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [address],
        })),
      });
      // A failed balanceOf on a real base token is an RPC problem, not a zero
      // balance: mapping it to 0n made RPC hiccups render as "0 DPS" in the
      // wallet/header. Throw so react-query retries and consumers see
      // pending/error (skeleton) instead of a fake zero.
      const failed = result.find(r => r.status !== 'success');
      if (failed) throw new Error('token balance multicall failed', { cause: failed.error });
      const validResultMap = new Map(validTokens.map((token, i) => [token.baseTokenAddress, result[i]]));
      return tokens.baseTokens.map(token => ({
        ...token,
        balance: validResultMap.has(token.baseTokenAddress)
          ? (validResultMap.get(token.baseTokenAddress)!.result as bigint)
          : 0n,
      }));
    },
  });
  return query;
};
